import React, { useCallback, useEffect, useState } from 'react'
import { Button, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import { ClockHistory } from 'react-bootstrap-icons'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import Badge from '../../components/_atoms/Badge/Badge'
import ListNoItems from '../../components/_atoms/ListNoItems/ListNoItems'
import ListPagination from '../../components/_atoms/ListPagination/ListPagination'
import ListSkeleton from '../../components/_atoms/ListSkeleton/ListSkeleton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import { ModalSaveClient } from '../../components/_molecules/ModalSaveClient/ModalSaveClient'
import ModalDeleteClient from '../../components/_molecules/ModalDeleteClient/ModalDeleteClient'
import SearchComponentRTK from '../../components/_molecules/SearchComponent/SearchComponentRTK'
import CellActionsClients from '../../components/_orgamisms/CellActionsClients/CellActionsClients'
import { fontSizes, fontWeights } from '../../constants/salesStyles'
import { ToastType, useToast } from '../../hooks/useToast'
import { useGetClientsListQuery } from '../../modules/store/dochubApi/dochubApi'
import { Client, ClientsSearchParams } from '../../modules/store/dochubApi/dochubTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import { formatDate } from '../../utils/converters'
import { colWidth } from '../../utils/stylePatches'

import styles from './styles.module.scss'

const ClientList = () => {
    const navigate = useNavigate()

    const [isModalSaveClientOpen, setModalSaveClientOpen] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [clientToEdit, setClientToEdit] = useState<Client | null>(null)
    const [clientToDelete, setClientToDelete] = useState<Client | null>(null)

    const [searchParams] = useSearchParams()
    const searchPage = Number(searchParams.get('page')) || 1
    const searchQuery = searchParams.get('search') || ''

    const [filters, setFilters] = useState<ClientsSearchParams>({
        pagination: {
            page: searchPage,
            perPage: 50,
        },
        search: searchQuery,
    })
    const {
        pagination: { page, perPage },
        search,
    } = filters

    const {
        data,
        isFetching: isLoadingGetClientsList,
        isError: isErrorGetClientsList,
        refetch,
    } = useGetClientsListQuery(
        {
            params: {
                pagination: { page, perPage },
                search,
            },
        },
        {
            refetchOnMountOrArgChange: true,
        },
    )

    const clientsList = data?.data?.filter(item => item.createdAt !== 0) || []
    const clientsCount = data?.dataCount || 0

    const setFiltersAndUpdateUrl = (newFilters: ClientsSearchParams) => {
        setFilters(newFilters)

        const newParams = new URLSearchParams()

        if (newFilters.pagination.page > 1) {
            newParams.set('page', String(newFilters.pagination.page))
        }
        if (newFilters.search) {
            newParams.set('search', newFilters.search)
        }

        navigate(
            {
                pathname: getAdminNavigationPath(adminRouteAlias.clients.location),
                search: newParams.toString(),
            },
            { replace: false },
        )
    }

    const setPage = (newPage: number) => {
        setFiltersAndUpdateUrl({
            ...filters,
            pagination: { ...filters.pagination, page: newPage },
        })
    }

    const setSearchTerm = (newTerm: string) => {
        setFiltersAndUpdateUrl({
            ...filters,
            search: newTerm,
            pagination: { ...filters.pagination, page: 1 },
        })
    }

    const renderListPagination = useCallback(() => {
        if (!isLoadingGetClientsList && clientsCount === 0) return null

        return (
            <div>
                <ListPagination
                    page={page}
                    setPage={setPage}
                    perPage={perPage}
                    itemsCount={clientsCount}
                    justifyContent="flex-end"
                />
            </div>
        )
    }, [clientsCount, isLoadingGetClientsList, page, perPage])

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                page: searchPage,
            },
            search: searchQuery,
        }))
    }, [searchPage, searchQuery])

    const openModalForEditing = (client: Client) => {
        setClientToEdit(client)
        setModalSaveClientOpen(true)
    }

    const openModalForCreating = () => {
        setClientToEdit(null)
        setModalSaveClientOpen(true)
    }

    const openDeleteModal = (client: Client) => {
        setClientToDelete(client)
        setIsDeleteModalOpen(true)
    }

    const openHistoryPage = () => {
        navigate(getAdminNavigationPath(adminRouteAlias.clients.history))
    }

    useToast({ isTriggered: isErrorGetClientsList, type: ToastType.ERROR, message: 'Ошибка загрузки клиентов' })

    const renderEllipsisCell = (text?: string) => (
        <OverlayTrigger placement="left" overlay={text ? <Tooltip>{text}</Tooltip> : <></>}>
            <span className={styles.ellipsis}>{text}</span>
        </OverlayTrigger>
    )

    const handleCellClick = (client: Client) => () => openModalForEditing(client)

    return (
        <div className={styles.wrap}>
            <ToastContainer position="bottom-center" autoClose={5000} />
            {isModalSaveClientOpen && (
                <ModalSaveClient
                    isOpen={isModalSaveClientOpen}
                    closeModal={() => setModalSaveClientOpen(false)}
                    onSuccess={refetch}
                    clientData={clientToEdit}
                />
            )}
            {isDeleteModalOpen && clientToDelete && (
                <ModalDeleteClient
                    client={clientToDelete}
                    isOpen={isDeleteModalOpen}
                    closeModal={() => setIsDeleteModalOpen(false)}
                    onSuccess={refetch}
                />
            )}

            <div className={styles.rowWrap}>
                <SalesText
                    text={'Клиенты'}
                    fontWeight={fontWeights.bold}
                    fontSize={fontSizes.xxl}
                    letterSpacing={-0.02}
                />
                <Button variant="primary" className={styles.addButton} onClick={openModalForCreating}>
                    + Добавить Клиента
                </Button>
                <Button variant="outline-primary" className={styles.historyButton} onClick={openHistoryPage}>
                    <ClockHistory size={18} />
                    История изменений
                </Button>
            </div>
            <div>
                <SearchComponentRTK
                    searchTerm={filters.search}
                    setSearchTerm={setSearchTerm}
                    placeholder="Поиск по клиентам"
                />
            </div>
            {renderListPagination()}
            <Table striped hover>
                <thead>
                    <tr>
                        <th scope="col" style={colWidth(100)}>
                            Номер
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Дата
                        </th>
                        <th scope="col" style={colWidth(150)}>
                            Название
                        </th>
                        <th scope="col" style={colWidth(150)}>
                            Телефон
                        </th>
                        <th scope="col" style={colWidth(150)}>
                            Email
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Примечание
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Заметка
                        </th>
                        <th scope="col" style={colWidth(120)}>
                            Действия
                        </th>
                    </tr>
                </thead>
                {isLoadingGetClientsList ? (
                    <ListSkeleton rowCount={9} columnCount={8} marginVerticalRem={0.21} />
                ) : (
                    clientsList && (
                        <tbody>
                            {clientsList.map((client, index) => (
                                <tr key={index} className={styles.row}>
                                    <td scope="row" onClick={handleCellClick(client)}>
                                        {client.code}
                                    </td>
                                    <td scope="row" onClick={handleCellClick(client)}>
                                        <Badge text={formatDate(client.createdAt)} color="#ddd" fontColor="#555" />
                                    </td>
                                    <td scope="row" onClick={handleCellClick(client)}>
                                        {client.name}
                                    </td>
                                    <td scope="row" onClick={handleCellClick(client)}>
                                        {client.phone}
                                    </td>
                                    <td scope="row" onClick={handleCellClick(client)}>
                                        {client.email}
                                    </td>
                                    <td scope="row" className={styles.ellipsis} onClick={handleCellClick(client)}>
                                        {renderEllipsisCell(client.meta?.publicComment)}
                                    </td>
                                    <td scope="row" className={styles.ellipsis} onClick={handleCellClick(client)}>
                                        {renderEllipsisCell(client.meta?.privateNote)}
                                    </td>
                                    <td scope="row">
                                        <CellActionsClients
                                            editModal={() => openModalForEditing(client)}
                                            deleteModal={() => openDeleteModal(client)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    )
                )}
            </Table>
            {!isLoadingGetClientsList && (clientsCount === 0 || !clientsList.length) ? (
                <ListNoItems resetFilters={() => navigate(getAdminNavigationPath(adminRouteAlias.clients.location))} />
            ) : (
                renderListPagination()
            )}
        </div>
    )
}

export default ClientList
